import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AssignSitesToSensorTypesComponent } from '../assign-sites-to-sensor-types/assign-sites-to-sensor-types.component';
import { SensorTypeDefinition, SensorTypeService } from '../services/sensorType.service';
import { Site } from '../services/site.service';

@Component({
    selector: 'app-sensor-types',
    templateUrl: './sensorTypes.component.html',
    styleUrls: ['./sensorTypes.component.css'],
    standalone: false
})
export class SensorTypesComponent implements OnInit, AfterViewInit {
  sensorTypes: MatTableDataSource<SensorTypeDefinition> = new MatTableDataSource();
  showLatestRevisionsOnly: boolean = false; // Add this line

  constructor(private sensorTypeService: SensorTypeService, public dialog: MatDialog, public snackBar: MatSnackBar) {}

  displayedColumns = [
    'id',
    'name',
    'sensorTypeId',
    'revision',
    'description',
    'measurementDefinitions',
    'modbusDeviceType',
    'sourceJson',
    'sites',
    'clone',
  ];

  ngOnInit() {
    this.fetchSensorTypes();
  }

  @ViewChild(MatSort) sort: MatSort;

  ngAfterViewInit() {
    this.sensorTypes.sort = this.sort;
  }

  fetchSensorTypes(): void {
    this.sensorTypeService.getSensorTypes().subscribe(
      (s) => {
        this.sensorTypes.data = this.filterSensorTypes(s);
      },
      (err: HttpErrorResponse) => {
        this.snackBar.open(`There was an error fetching sensorTypes ${JSON.stringify(err.error)}`, null, {
          duration: 10000,
        });
        console.error(err);
      }
    );
  }

  filterSensorTypes(sensorTypes: SensorTypeDefinition[]): SensorTypeDefinition[] {
    if (!this.showLatestRevisionsOnly) {
      return sensorTypes;
    }

    const latestSensorTypes = new Map<string, SensorTypeDefinition>();

    sensorTypes
      .filter((sensorType) => sensorType.sensorTypeId)
      .forEach((sensorType) => {
        const existing = latestSensorTypes.get(sensorType.sensorTypeId.toString());
        if (!existing || existing.revision < sensorType.revision) {
          latestSensorTypes.set(sensorType.sensorTypeId.toString(), sensorType);
        }
      });

    return Array.from(latestSensorTypes.values());
  }

  toggleShowLatestRevisionsOnly(): void {
    this.showLatestRevisionsOnly = !this.showLatestRevisionsOnly;
    this.sensorTypes.data = this.filterSensorTypes(this.sensorTypes.data);
  }

  getFormattedAssignedSites(sites: Site[]): string {
    return sites.map((p) => `${p.name} -> ${p.id}`).join('\n');
  }

  openAssignSitesDialog(sensorType: SensorTypeDefinition): void {
    const dialogRef = this.dialog.open(AssignSitesToSensorTypesComponent, {
      width: '1000px',
      data: { sensorType, isStandaloneDialog: true },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.fetchSensorTypes();
    });
  }

  openCopySensorTypeDialog(sensorType: SensorTypeDefinition): void {
    const dialogRef = this.dialog.open(NewModbusConfigDialogComponent, {
      width: '800px',
      data: { sensorType },
    });
    dialogRef.afterClosed().subscribe((success = false) => {
      if (success) {
        this.fetchSensorTypes();
      }
    });
  }
}
@Component({
    selector: 'azure-ad-new-modbus-configuration-dialog',
    template: `<mat-card appearance="outlined">
    <h3>New Modbus Configuration</h3>
    <mat-form-field appearance="fill" style="width: 99%;">
      <mat-label>ModbusConfiguration</mat-label>
      <textarea matInput rows="20" cols="30" [(ngModel)]="modbusConfig"></textarea>
    </mat-form-field>
    <div mat-dialog-content>
      <assign-sites-to-sensor-types-component
        [sensorType]="data.sensorType"
        (siteIdsChange)="onSiteIdsChange($event)"
      ></assign-sites-to-sensor-types-component>
    </div>

    <mat-card-actions>
      <button mat-raised-button color="primary" [disabled]="!modbusConfig" (click)="onSaveClick()">Create</button>
      <button mat-raised-button (click)="onCancelClick()">Cancel</button>
    </mat-card-actions>
  </mat-card> `,
    standalone: false
})
export class NewModbusConfigDialogComponent {
  modbusConfig: string;
  siteIds: string[] = [];

  onSiteIdsChange(siteIds: string[]): void {
    this.siteIds = siteIds;
  }

  constructor(
    public dialogRef: MatDialogRef<NewModbusConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { sensorType?: SensorTypeDefinition },
    private sensorTypeService: SensorTypeService,
    public snackBar: MatSnackBar
  ) {
    this.modbusConfig = data.sensorType.sourceJson
      ? JSON.stringify(JSON.parse(data.sensorType.sourceJson), null, '  ')
      : '';
  }

  onSaveClick(): void {
    try {
      try {
        JSON.parse(this.modbusConfig);
      } catch (error) {
        this.snackBar.open(
          `ERROR: There seems to be an error with the json formatting. Unable to parse JSON. ${error}`,
          null,
          {
            duration: 10000,
          }
        );
      }
      if (this.modbusConfig) {
        setTimeout(() => {
          this.sensorTypeService.addSensorTypes(this.modbusConfig, this.siteIds).subscribe(
            (s) => {
              this.snackBar.open(`Successfully added sensorType`, null, { duration: 10000 });
              console.log(s);
              this.dialogRef.close(true);
            },
            (err: Error) => {
              this.snackBar.open(
                `There was an error adding config ${JSON.stringify(
                  err instanceof HttpErrorResponse ? err.error : err
                )}`,
                null,
                {
                  duration: 10000,
                }
              );
              console.error(err);
            }
          );
        }, 1000);
      }
    } catch (err) {
      this.snackBar.open(`There seems to be an error with the json format: ${err?.message ?? err}`, null, {
        duration: 10000,
      });
    }
  }
  onCancelClick(): void {
    this.dialogRef.close();
  }
}
